import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  CircleAlert,
  Copy,
  Download,
  ExternalLink,
  FolderGit2,
  Github,
  LockKeyhole,
  Play,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import EmptyState from '../../components/site/EmptyState';
import SEO from '../../components/SEO';
import {
  codeLinkHost,
  codePath,
  codeShareUrl,
  fetchGithubCode,
  fetchGithubCodes,
} from '../../services/githubService';
import { getYouTubeVideoId } from '../../services/videoService';
import { formatDate } from '../../utils/format';
import { hasSession, loginPath } from '../../utils/session';
import './CodeDetailPage.css';

// Public landing page for one free-code entry: /code/:key (slug or id).
// This is the link that goes in YouTube descriptions.

const isUrl = value => /^https?:\/\//i.test(value || '');

const tagsOf = code =>
  Array.isArray(code?.tags)
    ? [...new Set(code.tags.filter(t => typeof t === 'string').map(t => t.trim()).filter(Boolean))]
    : [];

const summaryOf = text => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > 155 ? `${clean.slice(0, 152).trimEnd()}…` : clean;
};

/* ---------- Video: a poster until it's played, so the page stays fast ---------- */

const YouTubeEmbed = ({ id, title }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="cd-video">
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          className="cd-video__poster"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${title}`}
        >
          <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" />
          <span className="cd-video__play" aria-hidden="true">
            <Play />
          </span>
        </button>
      )}
    </div>
  );
};

/* ---------- Share ---------- */

const CopyLinkButton = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Older browsers: fall back to a temporary text field.
      const field = document.createElement('textarea');
      field.value = url;
      field.setAttribute('readonly', '');
      field.style.position = 'absolute';
      field.style.left = '-9999px';
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button type="button" className="cd-share" onClick={copy}>
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      <span aria-live="polite">{copied ? 'Link copied' : 'Copy link'}</span>
    </button>
  );
};

/* ---------- Get the code ---------- */

const GetCodeCard = ({ code, path }) => {
  const link = isUrl(code.link) ? code.link : null;
  const demo = isUrl(code.demoLink) && code.demoLink !== link ? code.demoLink : null;
  const video = isUrl(code.videoLink) ? code.videoLink : null;
  const isYouTube = Boolean(getYouTubeVideoId(code.videoLink));
  const host = link ? codeLinkHost(link) : null;
  // Locked: the server held the link back because this entry needs an account.
  const locked = Boolean(code.locked) || (code.requireSignIn && !hasSession());

  let primary;
  if (locked) {
    primary = (
      <>
        <p className="cd-get__text">
          This code is free with a Projects Kart account. Sign in or create one,
          and the link appears right here.
        </p>
        <Link
          className="pk-btn pk-btn--primary pk-btn--block"
          to={`${loginPath(path)}&mode=register`}
        >
          Create a free account
        </Link>
        <Link className="pk-btn pk-btn--secondary pk-btn--block" to={loginPath(path)}>
          I have an account
        </Link>
      </>
    );
  } else if (link) {
    const label =
      host.kind === 'github' || host.kind === 'git'
        ? `Open on ${host.name}`
        : host.kind === 'drive'
          ? `Download from ${host.name}`
          : 'Get the code';
    const Icon = host.kind === 'github' ? Github : host.kind === 'git' ? FolderGit2 : Download;
    primary = (
      <>
        <p className="cd-get__text">
          Free{host.name ? `, hosted on ${host.name}` : ''}. Opens in a new tab.
        </p>
        <a
          className="pk-btn pk-btn--primary pk-btn--block"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Icon aria-hidden="true" />
          {label}
        </a>
      </>
    );
  } else {
    primary = (
      <p className="cd-get__text">
        The code link isn’t available right now. Check back soon, or message us
        from the <Link to="/help#contact">Help Center</Link>.
      </p>
    );
  }

  return (
    <aside className="cd-get" aria-labelledby="cd-get-title">
      <div className="cd-get__head">
        <span className="cd-get__icon" data-locked={locked || undefined} aria-hidden="true">
          {locked ? <LockKeyhole /> : <Download />}
        </span>
        <h2 id="cd-get-title" className="cd-get__title">
          Get the source code
        </h2>
      </div>

      <div className="cd-get__body">{primary}</div>

      {code.requireSignIn && !locked && (
        <p className="cd-get__unlocked">
          <ShieldCheck aria-hidden="true" />
          Unlocked with your account
        </p>
      )}

      {(demo || (video && !isYouTube)) && (
        <div className="cd-get__more">
          {demo && (
            <a href={demo} target="_blank" rel="noopener noreferrer">
              <ExternalLink aria-hidden="true" />
              Live demo
            </a>
          )}
          {video && !isYouTube && (
            <a href={video} target="_blank" rel="noopener noreferrer">
              <Play aria-hidden="true" />
              Watch the video
            </a>
          )}
        </div>
      )}

      <div className="cd-get__foot">
        <CopyLinkButton url={codeShareUrl(code)} />
        {video && isYouTube && (
          <a className="cd-get__yt" href={video} target="_blank" rel="noopener noreferrer">
            Watch on YouTube
          </a>
        )}
      </div>
    </aside>
  );
};

/* ---------- More free code ---------- */

const MoreCode = ({ items }) => (
  <section className="cd-more" aria-labelledby="cd-more-title">
    <div className="cd-more__head">
      <h2 id="cd-more-title" className="pk-title-2">
        More free code
      </h2>
      <Link className="cd-more__all" to="/code">
        See all
        <ArrowRight aria-hidden="true" />
      </Link>
    </div>
    <ul className="cd-more__list">
      {items.map(item => (
        <li key={item.id || item.slug}>
          <Link className="cd-more__item" to={codePath(item)}>
            <span className="cd-more__title">{item.title}</span>
            {item.description && <span className="cd-more__desc">{item.description}</span>}
            <span className="cd-more__meta">
              {tagsOf(item).slice(0, 3).join(' · ')}
              {item.requireSignIn && (
                <span className="cd-more__lock">
                  <LockKeyhole aria-hidden="true" />
                  Free account
                </span>
              )}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

/* ---------- Page ---------- */

const CodeDetailPage = () => {
  const { key } = useParams();
  const { pathname } = useLocation();
  const [state, setState] = useState({ status: 'loading' });
  const [others, setOthers] = useState([]);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    const result = await fetchGithubCode(key);
    if (result.success) setState({ status: 'ready', code: result.data });
    else if (result.notFound) setState({ status: 'missing' });
    else setState({ status: 'error', error: result.error });
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  // The rest of the catalog, for "More free code". Failing quietly is fine here.
  useEffect(() => {
    let active = true;
    fetchGithubCodes().then(result => {
      if (active && result.success) setOthers(result.data);
    });
    return () => {
      active = false;
    };
  }, []);

  if (state.status === 'loading') {
    return (
      <main className="pk-page cd" aria-busy="true">
        <div className="pk-container">
          <span className="pk-skel cd-skel cd-skel--crumbs" />
          <span className="pk-skel cd-skel cd-skel--title" />
          <div className="cd-layout">
            <span className="pk-skel cd-skel cd-skel--video" />
            <span className="pk-skel cd-skel cd-skel--card" />
          </div>
        </div>
      </main>
    );
  }

  if (state.status !== 'ready') {
    const missing = state.status === 'missing';
    return (
      <main className="pk-page cd">
        <SEO title={missing ? 'Code link not found' : 'Free source code'} noindex />
        <div className="pk-container pk-container--narrow">
          <EmptyState
            role={missing ? undefined : 'alert'}
            icon={<CircleAlert />}
            title={missing ? 'This code link doesn’t exist' : 'Couldn’t load this code'}
            text={
              missing
                ? 'It may have been renamed or removed. Browse all the free code instead.'
                : state.error
            }
            actions={
              missing ? (
                <Link className="pk-btn pk-btn--primary pk-btn--sm" to="/code">
                  Browse free code
                </Link>
              ) : (
                <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={load}>
                  <RotateCcw aria-hidden="true" />
                  Try again
                </button>
              )
            }
          />
        </div>
      </main>
    );
  }

  const { code } = state;
  const tags = tagsOf(code);
  const youTubeId = getYouTubeVideoId(code.videoLink);
  const meta = [code.author?.trim(), code.createdAt ? formatDate(code.createdAt) : null].filter(
    value => value && value !== '—'
  );
  const more = others
    .filter(item => item.id !== code.id)
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 3);

  return (
    <main className="pk-page cd">
      <SEO
        title={`${code.title}: free source code`}
        description={summaryOf(code.description)}
        path={codePath(code)}
      />
      <div className="pk-container">
        <nav aria-label="Breadcrumb">
          <ol className="pk-crumbs">
            <li>
              <Link to="/">Home</Link>
            </li>
            <li>
              <Link to="/code">Free code</Link>
            </li>
            <li>
              <span aria-current="page">{code.title}</span>
            </li>
          </ol>
        </nav>

        <header className="cd-head">
          <h1 className="pk-head__title cd-title">{code.title}</h1>
          {(meta.length > 0 || tags.length > 0) && (
            <div className="cd-meta">
              {meta.length > 0 && <span>{meta.join(' · ')}</span>}
              {tags.length > 0 && (
                <ul className="cd-tags" aria-label="Tags">
                  {tags.map(tag => (
                    <li key={tag} className="pk-badge">
                      {tag}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </header>

        <div className="cd-layout" data-has-video={Boolean(youTubeId) || undefined}>
          {youTubeId && (
            <div className="cd-layout__media">
              <YouTubeEmbed id={youTubeId} title={code.title} />
            </div>
          )}

          <div className="cd-layout__side">
            <GetCodeCard code={code} path={pathname} />
          </div>

          {code.description && (
            <section className="cd-layout__about" aria-labelledby="cd-about-title">
              <h2 id="cd-about-title" className="cd-about__title">
                About this project
              </h2>
              <p className="cd-about__text">{code.description}</p>
            </section>
          )}
        </div>

        <section className="cd-upsell" aria-labelledby="cd-upsell-title">
          <div>
            <h2 id="cd-upsell-title" className="cd-upsell__title">
              Want the complete, ready-made project?
            </h2>
            <p className="cd-upsell__text">
              Our ready-made projects come with full source code, and each one lists
              what else is included, like documentation or setup videos.
            </p>
          </div>
          <div className="cd-upsell__actions">
            <Link className="pk-btn pk-btn--primary" to="/products">
              Browse projects
            </Link>
            <Link className="pk-btn pk-btn--secondary" to="/request-project">
              Request a custom build
            </Link>
          </div>
        </section>

        {more.length > 0 && <MoreCode items={more} />}
      </div>
    </main>
  );
};

export default CodeDetailPage;
